import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type {
  LueftungAnnahme, LueftungBewertung, LueftungElementziel, LueftungJ9Stand, LueftungLaufangabe,
  LueftungNormverweis, LueftungVariantenbewertung, LueftungVorbehalt,
} from '../../api';
import {
  ContentSection, DataTable, StatusBadge, annahmenVorbehalt, befundrang, bestaetigung, entfaellt,
  formatiereGroesse, formatiereZahl, herkunft, pruefergebnis, rechenstand, type Fachstatus, type Groesse,
  type Spalte, type Vorbehalt, type Vorbehaltsart,
} from '../../muster';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TV5 — GEMEINSAMES DER PRÜFUNGSSEITEN DER LÜFTUNG
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Vier Seiten (RLT-Geräteprüfung, Brandschutz, Luftmengen, Befunde) zeigen
 * dieselben Bausteine: Rechenstand im Kopf (S1.2), Vorbehalte, Annahmen und
 * Vorgaben (S1.6), Normbezug, Fachstatus (S1.5) und den Verweis auf das
 * Element. Sie stehen hier EINMAL, damit die vier Seiten sie nicht
 * auseinanderlaufen lassen.
 *
 * Die Texte der Stände sind dieselben wie in PDF und Excel
 * (`LueftungPruefungListen.Standtext`/`Bewertungstext`) — eine Zahl, eine
 * Darstellung (S0.5).
 */

const ZEIT = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  timeZone: 'Europe/Berlin',
});
const TAG = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin',
});

/** „18.10.2026, 06:07“ (Ortszeit Deutschland); leer bei einer Fehlstelle. */
export function zeitpunkt(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : ZEIT.format(d);
}

/** „05.09.2026“; leer bei einer Fehlstelle. */
export function tag(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : TAG.format(d);
}

/**
 * Der Rechenstand in der Pflichtform von S1.2: „Lauf ‹…› · gerechnet ‹…› ·
 * Modellstand ‹…›“; ohne Lauf „noch nicht gerechnet“.
 */
export function rechenstandText(lauf: LueftungLaufangabe | null | undefined): string {
  if (!lauf) return 'noch nicht gerechnet';
  const teile = [lauf.lauf, `gerechnet ${zeitpunkt(lauf.gerechnetAm)}`];
  if (lauf.modellstand) teile.push(`Modellstand ${tag(lauf.modellstand)}`);
  return teile.join(' · ');
}

/** Rechenstand als Text mit Marke (aktuell / veraltet / nicht gerechnet). */
export function Rechenstand({ lauf, datenhash }: { lauf: LueftungLaufangabe | null | undefined; datenhash?: string | null }) {
  const s = rechenstand(lauf?.gerechnetAm ?? null, lauf?.modellstand ?? null);
  return (
    <span data-rechenstand>
      {rechenstandText(lauf)}{' '}
      <StatusBadge {...s} klein />
      {datenhash ? <span className="m-leise"> · Datenhash {datenhash.substring(0, 16)}</span> : null}
    </span>
  );
}

// ═══ Fachstatus (S1.5) ═══════════════════════════════════════════════════════

const J9_TEXT: Record<LueftungJ9Stand, string> = {
  erfuellt: 'erfüllt',
  nicht_erfuellt: 'nicht erfüllt',
  nicht_pruefbar: 'nicht prüfbar',
  entfaellt: 'entfällt',
};

/** Der Text eines J9-Stands, wie in PDF und Excel. */
export function standtext(stand: LueftungJ9Stand): string {
  return J9_TEXT[stand] ?? stand;
}

/**
 * Ein J9-Stand als Marke. „entfällt“ ist neutral und braucht seinen Grund
 * (S1.5, 1.3) - er steht in der Meldung der Zeile und zusätzlich im Titel.
 */
export function j9Status(stand: LueftungJ9Stand, grund: string): Fachstatus & { titel?: string } {
  if (stand === 'entfaellt') return entfaellt(grund || 'Anforderung trifft nicht zu');
  return pruefergebnis(stand);
}

/** Die Schwere eines Befunds als Marke (F/W/H des Standards). */
export function schwereStatus(schwere: string): Fachstatus {
  if (schwere === 'fehler') return befundrang('F');
  if (schwere === 'warnung') return befundrang('W');
  return befundrang('H');
}

/** Der Stand einer Konsistenzliste (BSK, Entrauchung). Befund ist eine Warnung, nie rot (keine Bemessung). */
export function konsistenzStatus(stand: string): Fachstatus {
  if (stand === 'konsistent') return { zustand: 'gut', text: 'konsistent' };
  if (stand === 'hinweis') return { zustand: 'info', text: 'Hinweis' };
  return { zustand: 'warnung', text: 'Befund' };
}

const BEWERTUNG: Record<LueftungBewertung, Fachstatus> = {
  im_rahmen: { zustand: 'gut', text: 'im Rahmen' },
  ueber_soll: { zustand: 'warnung', text: 'über Soll' },
  unter_soll: { zustand: 'fehler', text: 'unter Soll' },
  unplausibel: { zustand: 'warnung', text: 'unplausibel' },
  fehlt: { zustand: 'warnung', text: 'Ist fehlt' },
  kein_soll: { zustand: 'neutral', text: 'kein Soll' },
  // TV4 (F-TV-L1): kein Mangel und keine Entwarnung - geprüft ist nur die Untergrenze.
  nur_untergrenze: { zustand: 'info', text: 'nur Untergrenze geprüft' },
};

/** Die Bewertung einer Luftmengenzeile als Marke (Texte wie im PDF). */
export function bewertungStatus(b: LueftungBewertung): Fachstatus {
  return BEWERTUNG[b] ?? { zustand: 'neutral', text: b };
}

// ═══ Sollart, CO₂, Varianten (TV5c) ══════════════════════════════════════════
//
// Texte wie im PDF und im Excel (`LueftungPruefungListen.Sollarttext`,
// `Co2Stufentext`, `Variantentext`). Gelesen aus dem Lauf luftmengen-pruefen
// (TV4); die Seite entscheidet nichts.

const SOLLART: Record<string, string> = {
  auslegung: 'Auslegung',
  untergrenze: 'Untergrenze',
  extern: 'Fachsimulation',
  sanitaer: 'WC/Dusche-Maßstab',
  bereich: 'Bereich',
};

/** Was ein Sollwert ist (TV4); ohne Angabe (Lauf vor TV4) „ohne Angabe“. */
export function sollarttext(a: string | null | undefined): string {
  if (!a) return 'ohne Angabe';
  return SOLLART[a] ?? a;
}

const CO2_STUFE: Record<string, string> = {
  bis_1000: 'bis 1.000 ppm',
  bis_2000: 'über 1.000 bis 2.000 ppm',
  ueber_2000: 'über 2.000 ppm',
};

/** Die CO₂-Stufe nach ASR A3.6 Tabelle 2 als Text; ohne Stufe leer. */
export function co2Stufentext(stufe: string | null | undefined): string {
  if (!stufe) return '';
  return CO2_STUFE[stufe] ?? stufe;
}

const VARIANTE: Record<string, string> = {
  mvstaettv: 'Versammlungsraum mit Personendichte nach MVStättV',
  kat_i: 'DIN EN 16798-1 Kategorie I',
  kat_ii: 'DIN EN 16798-1 Kategorie II',
  kat_iii: 'DIN EN 16798-1 Kategorie III',
  sanitaer_flaeche: 'WC/Dusche je Fläche',
  sanitaer_objekt: 'WC/Dusche je Objekt',
  vdi2052: 'Küche aus der Anschlussleistung (VDI 2052)',
  qb_gesamt: 'Flächenwert als gesamte Luftmenge',
  bereich_raeume_zuluft: 'Bereich: Zuluft der Bereichsräume',
  bereich_geraete_aussenluft: 'Bereich: Außenluft der Geräte',
};

/** Was eine Variante rechnet. */
export function variantentext(v: string): string {
  return VARIANTE[v] ?? v;
}

/**
 * Das Ergebnis einer Variante als Marke - immer NEUTRAL: Eine Variante ist
 * nicht entschieden und kein Befund (TV5c, keine Entscheidungslogik ohne
 * Fachfreigabe). Der Text ist derselbe wie bei der Hauptzeile.
 */
export function variantenStatus(b: LueftungVariantenbewertung): Fachstatus {
  if (b === 'nicht_rechenbar') return { zustand: 'neutral', text: 'nicht rechenbar' };
  return { zustand: 'neutral', text: bewertungStatus(b).text };
}

// ═══ Zahlen mit Einheit aus der Zeile ════════════════════════════════════════

const GROESSE_JE_EINHEIT: Record<string, Groesse> = {
  'm/s': 'geschwindigkeit',
  'dB(A)': 'schallpegel_dba',
  dB: 'schallpegel_db',
  'm³/h': 'volumenstrom_luft',
  Pa: 'druck_pa',
  '%': 'anteil',
  'm²': 'flaeche',
  'm³': 'volumen',
};

/**
 * Eine Zahl, deren Einheit in der ZEILE steht (Befundlisten mischen m/s,
 * dB(A) und m³/h): Nachkommastellen aus dem Einheitenkatalog (S1.7), sonst die
 * Zahl selbst. Die Einheit steht in ihrer eigenen Spalte.
 */
export function zahlNachEinheit(wert: number | null | undefined, einheit: string | null | undefined): string {
  const g = einheit ? GROESSE_JE_EINHEIT[einheit] : undefined;
  return g ? formatiereGroesse(wert, g) : formatiereZahl(wert);
}

// ═══ Verweise ════════════════════════════════════════════════════════════════

/**
 * Der Verweis einer Zeile auf ihr Element: das Bauteil (Lüftungskomponente)
 * und/oder die Anlage (Anlagenseite). Ohne Ziel steht der Grund statt eines
 * leeren Feldes.
 */
export function Elementverweis({
  projektId, ziel, kennung, ohne = 'kein Modellelement',
}: {
  projektId: string;
  ziel: LueftungElementziel;
  /** Die Kennung, die der Verweis zeigt (Elementnummer, Anlage). */
  kennung?: string | null;
  ohne?: string;
}) {
  const teile: ReactNode[] = [];
  if (ziel.componentId) {
    teile.push(
      <Link key="b" to={`/projekte/${projektId}/lueftung/komponente/${ziel.componentId}`}>
        {kennung ? `Bauteil ${kennung}` : 'Bauteil'}
      </Link>,
    );
  }
  if (ziel.plantId) {
    teile.push(
      <Link key="a" to={`/projekte/${projektId}/anlagen/${ziel.plantId}`}>Anlagenseite</Link>,
    );
  }
  if (teile.length === 0) {
    return <span className="m-leise">{kennung ? `${kennung} · ${ohne}` : ohne}</span>;
  }
  return <span data-verweis>{teile.flatMap((t, i) => (i === 0 ? [t] : [' · ', t]))}</span>;
}

/** Die bestehenden Fachseiten zu einer Befundregel (Druckverlust, Schall, Raumschall, Anlagenliste). */
export function befundSeiten(regel: string): { pfad: string; name: string }[] {
  if (regel === 'raumpegel_ziel') {
    return [{ pfad: 'lueftung/raumschall', name: 'Raumschall' }, { pfad: 'lueftung/schall', name: 'Schall' }];
  }
  if (regel.startsWith('vdi6022_')) return [{ pfad: 'lueftung/anlagen', name: 'Anlagenliste' }];
  if (regel.startsWith('v_') || regel.endsWith('_kennfeld')) {
    return [{ pfad: 'lueftung/druckverlust', name: 'Druckverlust' }];
  }
  return [];
}

// ═══ Vorbehalte (TV5b, TX-STANDARD 1.6 S1.2 Nr. 2) ═══════════════════════════
//
// Bis TV5b bauten die vier Seiten einen eigenen Kasten (`hinweis warnung`) mit
// eigenen Sätzen. Jetzt kommen die Vorbehalte aus dem Datenmodell - dieselben
// Einträge wie im Vorbehaltskasten des PDF - und das Muster `Vorbehalte`
// (TX3) setzt sie: Etikett je Art, Reihenfolge nach Art, „(siehe …)“.

/** Name und Anker des Abschnitts „Annahmen und Vorgaben“ - Verweis und Ziel im Vorbehaltskasten. */
export const ANNAHMEN_TITEL = 'Annahmen und Vorgaben';
export const ANNAHMEN_ANKER = 'annahmen';

/** TV5c: Name und Anker des Abschnitts der Varianten (wie `LueftungPruefungListen.VariantenTitel`). */
export const VARIANTEN_TITEL = 'Varianten (nicht entschieden)';
export const VARIANTEN_ANKER = 'varianten';

const VORBEHALTSARTEN: Vorbehaltsart[] = ['annahmen', 'fachfrage', 'raumstand', 'veraltet', 'sonstiges'];

/** Die Art eines Vorbehalts des Datenmodells (Zahl der Aufzählung `Vorbehaltsart`) als Art des Musters. */
export function vorbehaltsart(art: number): Vorbehaltsart {
  return VORBEHALTSARTEN[art] ?? 'sonstiges';
}

function eintrag(v: LueftungVorbehalt): Vorbehalt {
  const art = vorbehaltsart(v.art);
  return {
    art,
    text: v.text,
    verweis: v.verweis ?? undefined,
    ziel: art === 'annahmen' ? `#${ANNAHMEN_ANKER}`
      : v.verweis === VARIANTEN_TITEL ? `#${VARIANTEN_ANKER}` : undefined,
  };
}

/**
 * Die Einträge des Vorbehaltskastens einer Seite aus ihren Datenmodellen. Mit EINEM Modell genau dessen
 * Vorbehalte, Satz für Satz wie im PDF. Zeigt eine Seite zwei Modelle (BSK und Entrauchung), steht der
 * Annahmensatz einmal für die Annahmen beider - so wie der Abschnitt „Annahmen und Vorgaben“ beide zeigt -,
 * und gleiche Vorbehalte (der Datenstand desselben Laufs) stehen nur einmal.
 */
export function seitenVorbehalte(
  modelle: { vorbehalte: LueftungVorbehalt[]; annahmen: LueftungAnnahme[] }[],
): Vorbehalt[] {
  if (modelle.length === 1) return modelle[0].vorbehalte.map(eintrag);

  const aus: Vorbehalt[] = [];
  const gesehen = new Set<string>();
  for (const v of modelle.flatMap((m) => m.vorbehalte)) {
    const e = eintrag(v);
    const schluessel = `${e.art}|${e.text}|${e.verweis ?? ''}`;
    if (e.art === 'annahmen' || gesehen.has(schluessel)) continue;
    gesehen.add(schluessel);
    aus.push(e);
  }

  const annahmen = modelle.flatMap((m) => m.annahmen);
  const offen = annahmen.filter((a) => !a.bestaetigt).length;
  if (offen > 0 && modelle.some((m) => m.vorbehalte.some((v) => vorbehaltsart(v.art) === 'annahmen'))) {
    aus.unshift(annahmenVorbehalt(offen, annahmen.length, ANNAHMEN_TITEL, `#${ANNAHMEN_ANKER}`));
  }
  // In der Reihenfolge des Kastens (nach Art, sonst wie geliefert) - so, wie das Muster sie setzt.
  const rang = (v: Vorbehalt) => VORBEHALTSARTEN.indexOf(v.art);
  return aus.map((v, i) => ({ v, i })).sort((x, y) => rang(x.v) - rang(y.v) || x.i - y.i).map(({ v }) => v);
}

// ═══ Annahmen, Normbezug ═════════════════════════════════════════════════════

/** Abschnitt „Annahmen und Vorgaben“ (S1.6): dieselbe Liste wie im PDF und im Excel-Blatt. */
export function AnnahmenAbschnitt({ annahmen, name }: { annahmen: LueftungAnnahme[]; name: string }) {
  // Unbestätigte zuerst, sonst in der Reihenfolge des Datenmodells (wie PDF/Excel).
  const zeilen = useMemo(
    () => annahmen.map((a, i) => ({ a, i })).sort((x, y) => Number(x.a.bestaetigt) - Number(y.a.bestaetigt) || x.i - y.i),
    [annahmen],
  );
  const spalten = useMemo<Spalte<{ a: LueftungAnnahme; i: number }>[]>(() => [
    { schluessel: 'parameter', titel: 'Parameter', wert: (z) => z.a.parameter, immerSichtbar: true },
    { schluessel: 'wert', titel: 'Wert', wert: (z) => z.a.wert ?? '', ausrichtung: 'rechts' },
    { schluessel: 'einheit', titel: 'Einheit', wert: (z) => z.a.einheit ?? '' },
    {
      schluessel: 'herkunft', titel: 'Herkunft', art: 'status', wert: (z) => z.a.herkunft,
      inhalt: (z) => <StatusBadge {...herkunft(z.a.herkunft)} klein />,
    },
    {
      schluessel: 'quelle', titel: 'Quelle / Norm',
      wert: (z) => [z.a.quelle, z.a.normbezug].filter(Boolean).join(' · '),
    },
    {
      schluessel: 'bestaetigt', titel: 'Bestätigung', art: 'status', wert: (z) => z.a.bestaetigt,
      inhalt: (z) => <StatusBadge {...bestaetigung(z.a.bestaetigt)} klein />,
    },
  ], []);

  return (
    <ContentSection
      id={ANNAHMEN_ANKER}
      titel={ANNAHMEN_TITEL}
      beschreibung="Alle genutzten Vorgaben mit Herkunft, Quelle und Bestätigung, die unbestätigten zuerst - dieselbe Liste wie im PDF und im Excel-Blatt „Annahmen“."
    >
      <DataTable
        name={`Annahmen ${name}`}
        spalten={spalten}
        zeilen={zeilen}
        schluessel={(z) => `${z.i}`}
        ausfuhr={zeilen.length > 20}
        ausfuhrDateiname={`annahmen-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`}
        leer={<span>Diese Prüfung nutzt keine Vorgaben.</span>}
      />
    </ContentSection>
  );
}

/** Abschnitt „Normbezug“: Norm, Ausgabe, Abschnitt, Anwendung (S3.3 Punkt 3, Excel-Blatt „Normbezug“). */
export function NormAbschnitt({ normen, name }: { normen: LueftungNormverweis[]; name: string }) {
  const zeilen = useMemo(() => normen.map((n, i) => ({ n, i })), [normen]);
  const spalten = useMemo<Spalte<{ n: LueftungNormverweis; i: number }>[]>(() => [
    { schluessel: 'norm', titel: 'Norm', wert: (z) => z.n.norm, immerSichtbar: true },
    { schluessel: 'ausgabe', titel: 'Ausgabe', wert: (z) => z.n.ausgabe ?? '' },
    { schluessel: 'abschnitt', titel: 'Abschnitt', wert: (z) => z.n.abschnitt ?? '' },
    { schluessel: 'anwendung', titel: 'Anwendung', wert: (z) => z.n.anwendung },
  ], []);

  return (
    <ContentSection titel="Normbezug" beschreibung="Die Normen und Fundstellen, auf die sich die Prüfung stützt.">
      <DataTable
        name={`Normbezug ${name}`}
        spalten={spalten}
        zeilen={zeilen}
        schluessel={(z) => `${z.i}`}
        leer={<span>Für diese Prüfung ist kein Normbezug hinterlegt.</span>}
      />
    </ContentSection>
  );
}

/** Werte einer Spalte als Auswahl für einen Filter: sortiert, ohne Leere, ohne Doppel. */
export function optionenAus(werte: (string | null | undefined)[]): { wert: string; beschriftung: string }[] {
  return [...new Set(werte.filter((w): w is string => !!w))]
    .sort((a, b) => a.localeCompare(b, 'de', { numeric: true }))
    .map((w) => ({ wert: w, beschriftung: w }));
}
